import { FormEvent, useState } from "react";
import { ProviderIntegrationApi } from "../../../service/Api/ProviderIntegrationApi";
import { CptCodeSearchResultApi } from "../types/providerIntegrationtypes";

interface CptSearchBarProps {
  conflicts?: number;
  totalCodes?: number;
  onSelectMatch?: (match: CptCodeSearchResultApi) => void;
}

const formatProviderLabel = (match: CptCodeSearchResultApi) => {
  const name = match.providerName?.trim();
  const code = match.providerCode?.trim();
  if (name && code) return `${name} (${code})`;
  if (name) return name;
  if (code) return code;
  return "Unknown provider";
};

const CptSearchBar = ({
  totalCodes = 0,
  onSelectMatch,
}: CptSearchBarProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CptCodeSearchResultApi[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const clearResults = () => {
    setResults(null);
    setSearchError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) {
      clearResults();
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    try {
      const matches = await ProviderIntegrationApi.searchCptCodes(trimmed);
      setResults(Array.isArray(matches) ? matches : []);
    } catch {
      setResults(null);
      setSearchError("Unable to search CPT/HCPCS codes. Try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    clearResults();
  };

  const handleSelect = (match: CptCodeSearchResultApi) => {
    onSelectMatch?.(match);
    clearResults();
  };

  return (
    <div className="ic-cpt-toolbar">
      <div className="ic-cpt-search-wrap">
        <form className="ic-cpt-search" onSubmit={handleSubmit}>
          <div className="ic-filter-input-wrap">
            <i className="fh_search ic-filter-input-icon" aria-hidden="true"></i>
            <input
              type="text"
              className="form-control ic-cpt-input ic-filter-input"
              placeholder="Find CPT/HCPCS code..."
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                if (searchError) setSearchError(null);
              }}
              aria-label="Find CPT or HCPCS code"
              aria-expanded={results != null}
              aria-controls="ic-cpt-search-results"
            />
            {(query || searchError || results) && (
              <button
                type="button"
                className="ic-filter-clear-btn"
                onClick={handleClear}
                aria-label="Clear search"
                title="Clear"
              >
                <i className="fh_close" aria-hidden="true"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="btn btn-primary ic-cpt-search-btn"
            disabled={isSearching}
          >
            {isSearching ? "Searching..." : "Search"}
          </button>
        </form>

        {searchError && (
          <div
            className="ic-cpt-search-results ic-cpt-search-results--message"
            role="alert"
          >
            <div className="ic-cpt-search-results-empty ic-cpt-search-results-empty--error">
              <span>{searchError}</span>
              <button
                type="button"
                className="ic-cpt-search-results-dismiss"
                onClick={clearResults}
                aria-label="Dismiss error"
                title="Dismiss"
              >
                <i className="fh_close" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        )}

        {!searchError && results && (
          <div
            id="ic-cpt-search-results"
            className="ic-cpt-search-results"
            role="listbox"
            aria-label="CPT search matches"
          >
            <div className="ic-cpt-search-results-header">
              <span>Matches ({results.length})</span>
              <button
                type="button"
                className="ic-cpt-search-results-dismiss"
                onClick={clearResults}
                aria-label="Close matches"
                title="Close"
              >
                <i className="fh_close" aria-hidden="true"></i>
              </button>
            </div>
            {results.length === 0 ? (
              <div className="ic-cpt-search-results-empty" role="status">
                No CPT/HCPCS match found for “{query.trim()}”.
              </div>
            ) : (
              <ul className="ic-cpt-search-results-list">
                {results.map((match) => (
                  <li key={`${match.providerId}-${match.id}`}>
                    <button
                      type="button"
                      className="ic-cpt-search-result-row"
                      role="option"
                      onClick={() => handleSelect(match)}
                    >
                      <span className="ic-cpt-search-result-code">
                        {match.cptCode}
                      </span>
                      <span className="ic-cpt-search-result-type">
                        {match.codeType}
                      </span>
                      <span className="ic-cpt-search-result-provider">
                        {formatProviderLabel(match)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="ic-cpt-meta">
        <span>
          Total codes: <strong>{totalCodes}</strong>
        </span>
      </div>
    </div>
  );
};

export default CptSearchBar;
